import { createSlice } from '@reduxjs/toolkit';
import { createTransform, persistReducer } from 'redux-persist';

import { memoize } from 'proxy-memoize';

import { RootState } from '..';
import { AuthActions } from '../auth/auth.redux';
import { storage } from '../store.persist';

type SearchFilterState = { params: Record<string, any> };

/**
 * Inbound: when persisting, transform receives the value of each key.
 * For key 'params', state = { workerApplicant: {...}, workerFranchise: {...} }.
 * Omit `page` from each screen so pagination is not restored.
 */
const _searchFilterTransform = createTransform<
	Record<string, any>,
	Record<string, any>,
	SearchFilterState
>(
	(state, key) => {
		if (key !== 'params' || !state || typeof state !== 'object') return state;
		const paramsWithoutPage: Record<string, any> = {};
		for (const [screen, screenParams] of Object.entries(state)) {
			if (screenParams && typeof screenParams === 'object') {
				const { page: _page, ...rest } = screenParams;
				paramsWithoutPage[screen] = rest;
			} else {
				paramsWithoutPage[screen] = screenParams;
			}
		}
		return paramsWithoutPage;
	},
	null,
	{ whitelist: ['params'] }
);

const initialState = {
	params: {} as Record<string, any>,
};

const searchFilterSlice = createSlice({
	initialState,
	name: 'searchFilter',
	reducers: {
		setSearchFilterParams: (state, action) => {
			state.params[action.payload.screen] = {
				...(state.params[action.payload.screen] || {}),
				...action.payload.params,
			};
		},
		clearSearchFilterParams: (state, action) => {
			state.params[action.payload.screen] = {};
		},
		clearAllSearchFilterParams: (state) => {
			state.params = {};
		},
		/** Remove only `page` for one screen; other params are kept. */
		clearPageOnly: (state, action) => {
			const screenParams = state.params[action.payload.screen];
			if (screenParams && typeof screenParams === 'object') {
				const { page: _page, ...rest } = screenParams;
				state.params[action.payload.screen] = rest;
			}
		},
		/** Remove only `page` for all screens; other params are kept. */
		clearAllPagesOnly: (state) => {
			for (const screen of Object.keys(state.params)) {
				const screenParams = state.params[screen];
				if (screenParams && typeof screenParams === 'object') {
					const { page: _page, ...rest } = screenParams;
					state.params[screen] = rest;
				}
			}
		},
	},
	extraReducers(builder) {
		builder.addCase(AuthActions.logoutSuccess, (state) => {
			state.params = {};
		});
	},
});

export const SearchFilterSelectors = {
	selectSearchFilterParams: (screen: string) =>
		memoize((state: RootState) => state.searchFilter.params?.[screen] || {}),
};

export const SearchFilterActions = searchFilterSlice.actions;
export const SEARCH_FILTER_PERSIST_KEY = 'searchFilterPersist';
export const SearchFilterReducer = persistReducer(
	{
		key: SEARCH_FILTER_PERSIST_KEY,
		storage: storage,
		whitelist: ['params'],
	},
	searchFilterSlice.reducer
);
